import { format, parseISO, isSameDay } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { X, Plus } from 'lucide-react';

import { getScheduleLabel, mergeCalendarEvents } from '../../utils/scheduleEvents';

function DayView({ date, onClose, onEventClick }) {
  const { user } = useAuthStore();

  const { data: schedules, isLoading } = useQuery({
    queryKey: ['schedules', format(date, 'yyyy-MM-dd'), 'day'],
    queryFn: async () => {
      const startDate = format(date, 'yyyy-MM-dd');
      const endDate = format(date, 'yyyy-MM-dd');
      const workerRes =
        user?.role === 'WORKER'
          ? await api.get(`/workers/${user.id}/schedule?startDate=${startDate}&endDate=${endDate}`)
          : await api.get(`/workers/schedules?startDate=${startDate}&endDate=${endDate}`);
      const workerSchedules = workerRes.data.data || [];
      if (user?.role === 'WORKER') return workerSchedules;
      const [jobRes, mandateRes] = await Promise.all([
        api.get(`/jobs/schedule-events?startDate=${startDate}&endDate=${endDate}`),
        api.get(`/mandates/schedule-events?startDate=${startDate}&endDate=${endDate}`),
      ]);
      return mergeCalendarEvents(workerSchedules, jobRes.data.data || [], mandateRes.data.data || []);
    },
  });

  const parseScheduleDate = (dt) => {
    if (!dt) return null;
    const s = String(dt);
    const iso = s.includes(' ') ? s.replace(' ', 'T').slice(0, 19) : s;
    try {
      return parseISO(iso);
    } catch {
      return new Date(dt);
    }
  };
  const daySchedules = schedules?.filter(schedule => {
    const scheduleDate = parseScheduleDate(schedule.start_time);
    return scheduleDate && isSameDay(scheduleDate, date);
  }) || [];

  return (
    <div className="bg-card rounded-lg shadow">
      <div className="flex items-center justify-between p-6 border-b">
        <h2 className="text-2xl font-bold text-gray-900">
          {format(date, 'EEEE, MMMM d, yyyy')}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="text-center py-12">Loading...</div>
        ) : daySchedules.length > 0 ? (
          <div className="space-y-4">
            {daySchedules.map(schedule => (
              <div
                key={schedule.id}
                onClick={() => onEventClick(schedule)}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {getScheduleLabel(schedule)}
                    </h3>
                    {(schedule.building_name || schedule.ss_name || schedule.ss_code || schedule.iqp_name) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-2">
                        {schedule.building_name && <span>Building: {schedule.building_name}</span>}
                        {(schedule.ss_name || schedule.ss_code) && (
                          <span>SS: {schedule.ss_name || schedule.ss_code}</span>
                        )}
                        {schedule.iqp_name && <span>IQP: {schedule.iqp_name}</span>}
                      </div>
                    )}
                    {schedule.description && (
                      <p className="text-sm text-gray-600 mb-2">{schedule.description}</p>
                    )}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span>
                        {format(parseScheduleDate(schedule.start_time) || new Date(), 'h:mm a')} - {format(parseScheduleDate(schedule.end_time) || new Date(), 'h:mm a')}
                      </span>
                      {schedule.worker_name && (
                        <span>Worker: {schedule.worker_name}</span>
                      )}
                      {schedule.job_id && (
                        <span>Inspection ID: {schedule.job_id}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No schedules for this day</p>
            <button
              onClick={() => {
                // Trigger add schedule modal
                onEventClick({ start_time: format(date, "yyyy-MM-dd'T'00:00") });
              }}
              className="flex items-center mx-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Schedule
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DayView;
